import React, { FC, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import triggerTracking from '@blueprint/tracking';
import { useModal } from '@blueprint/ui';

import {
    XhrResponseModalContent,
    ResponseType
} from './XhrResponseModalContent';
import { filtersValuesSelector } from '../../reducers/filters';
import { polyencSelector } from '../../reducers/searchResults';
import { SearchContext } from '../SearchContext';
import { trackEvent } from 'shared/helpers/segment/trackEvent';

import { constructEditSavedSearchFields } from '../helpers';
import { alertLinkClickHandler } from '../common/alertLinkClickHandler';
import AlertModal from 'shared/components/AlertModal';

import { CREATE_SAVED_SEARCH } from 'queries/search/editSavedSearch';
import { CREATE_EMAIL_ALERT } from 'queries/search/editCreateAlert';
import { selectTaxonomy } from 'reducers/taxonomy';

import { EventNames } from 'shared/tracking/contracts';
import { useIsLoggedIn } from 'shared/hooks/useIsLoggedIn';
import { AjaxLinkClickProps, SimpleLinkClickProps } from './AlertPanel.types';
import { StyledModal } from './AlertPanel.styles';

const fireTrackingEvent = (category: string, label: string) => {
    triggerTracking({
        event: 'GAEvent',
        eventCategory: category,
        eventAction: 'clickthrough',
        eventLabel: label
    });
};

const SaveSearchAlertModalWrapper: FC = ({ children }) => {
    const [responseModalType, setResponseModalType] = useState<ResponseType>(
        null
    );

    const { state } = useContext(SearchContext);
    const router = useRouter();
    const isLoggedIn = useIsLoggedIn();

    const [isAlertModalShowing, , closeAlertModal] = useModal();
    const taxonomyForSaveSearch = useSelector(selectTaxonomy);
    const polyenc = useSelector(polyencSelector);
    const { push, asPath } = router;

    const { userAlertId, searchType } = state;

    const filters = useSelector(filtersValuesSelector);

    const onSaveSearchComplete = (status: number) => {
        if (status === 200) {
            setResponseModalType('saveSearchSuccess');
        } else {
            setResponseModalType('saveSearchFailure');
        }
    };

    const onSaveAlertComplete = (status: number) => {
        if (status === 200) {
            setResponseModalType('saveAlertSuccess');
        } else {
            setResponseModalType('saveAlertFailure');
        }
    };

    const [createSavedSearchMutation] = useMutation(CREATE_SAVED_SEARCH, {
        onCompleted: (data) => {
            onSaveSearchComplete(data.createNewSavedSearch.status);
        },
        onError: () => {
            setResponseModalType('saveSearchFailure');
        }
    });

    const [createEmailAlertMutation] = useMutation(CREATE_EMAIL_ALERT, {
        onCompleted: (data) => {
            onSaveAlertComplete(data.createNewEmailAlert.status);
        },
        onError: () => {
            setResponseModalType('saveAlertFailure');
        }
    });

    const onSimpleLinkClick: SimpleLinkClickProps = (
        evt,
        category,
        label,
        requiresAuth = false
    ) => {
        evt.preventDefault();

        if (label && category) {
            fireTrackingEvent(category, label);
        }

        alertLinkClickHandler({
            evt,
            router,
            requiresAuth,
            loggedIn: !!isLoggedIn
        });
    };

    const createSavedSearchOrAlert = (label?: string) => {
        const input = constructEditSavedSearchFields({
            userAlertId,
            searchType,
            filters,
            createNew: true,
            polyenc
        });

        if (label !== 'create_alert') {
            createSavedSearchMutation({
                variables: {
                    input
                }
            });
        } else {
            createEmailAlertMutation({
                variables: {
                    input
                }
            });
        }
    };

    const onAjaxLinkClick: AjaxLinkClickProps = async (
        evt,
        category,
        label
    ) => {
        evt.preventDefault();

        const destinationUrl = evt.currentTarget.dataset.url as string;
        const redirectUrl = encodeURIComponent(destinationUrl || asPath);
        const registerUrl = `${
            process.env.REGISTER_PATH || '/register/'
        }?page_after_login=${redirectUrl}&return_url=${redirectUrl}`;

        if (label && category) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: category,
                eventAction: 'clickthrough',
                eventLabel: label
            });

            if (label === 'save_search') {
                trackEvent(
                    EventNames.CLICK_SAVE_SEARCH,
                    taxonomyForSaveSearch.taxonomy
                );
            }
        }

        if (!isLoggedIn) {
            if (label === 'save_search') {
                push(registerUrl);
                return;
            }

            if (label === 'create_alert') {
                push(registerUrl);
                return;
            }
        }
        createSavedSearchOrAlert(label);
    };

    const onModalCloseClick = () => {
        setResponseModalType(null);
    };

    return (
        <>
            <AlertModal
                isShowing={isAlertModalShowing}
                close={closeAlertModal}
            />
            {React.Children.map(children, (child) => {
                if (React.isValidElement(child))
                    return React.cloneElement(child, {
                        onSimpleLinkClick: onSimpleLinkClick,
                        onAjaxLinkClick: onAjaxLinkClick
                    });
            })}
            <StyledModal
                isShowing={responseModalType !== null}
                close={onModalCloseClick}
            >
                <XhrResponseModalContent
                    responseType={responseModalType}
                    close={onModalCloseClick}
                    action={
                        responseModalType === 'saveSearchFailure'
                            ? () => createSavedSearchOrAlert('save_search')
                            : responseModalType === 'saveAlertFailure'
                            ? () => createSavedSearchOrAlert('save_alert')
                            : undefined
                    }
                />
            </StyledModal>
        </>
    );
};

export default SaveSearchAlertModalWrapper;
